import React, { useState } from "react";
import {
  DataField,
  ToggleField,
  formatCurrency,
  formatRange,
} from "../shared/DataField";
import EditControls from "../shared/EditControls";

import { TAX_BRACKET_OPTIONS, TAX_BRACKET_MAP, SOURCE_OF_WEALTH_OPTIONS } from "../../../constants/householdConstants";
import {
  getBrokerageFlags,
  showAnnualExpenseRange,
  showBankingEstimates,
  showSpecialExpenseRange,
  showExperienceField,
  showGoldmanQualifiers,
  showHouseholdSourceOfWealth,
  showIncomeDetails,
} from "../../../utils/brokerageFields";

const taxBracketToDisplay = (taxInfo) => {
  const bracket = taxInfo?.tax_bracket;
  if (!bracket || bracket.start == null) return "N/A";
  const entry = Object.entries(TAX_BRACKET_MAP).find(
    ([, v]) => v.start === bracket.start && v.end === bracket.end,
  );
  return entry ? entry[0] : `${bracket.start}-${bracket.end}%`;
};

const SUB_TABS = [
  { key: "incomeDetails", label: "Income Details" },
  { key: "qualifyingQuestions", label: "Qualifying Questions" },
  { key: "investmentExperience", label: "Investment Experience" },
];

const ReviewHouseholdInfoTab = ({
  householdData,
  isEditing,
  formData,
  onChange,
  onEdit,
  onSave,
  onCancel,
  saving,
  validationErrors,
  onFieldError,
}) => {
  const [subTab, setSubTab] = useState("incomeDetails");

  const household = householdData || {};
  const brokerageFlags = getBrokerageFlags(household);

  const getFormValue = (path, fallback) => {
    if (!formData) return fallback;
    const keys = path.split(".");
    let current = formData;
    for (const key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    return current !== undefined ? current : fallback;
  };

  const handleFieldChange = (fieldPath, value) => {
    if (onChange) onChange(fieldPath, value);
  };

  const handleTaxBracketChange = (_fieldPath, displayValue) => {
    const range = TAX_BRACKET_MAP[displayValue];
    if (range && onChange) {
      onChange("tax_info.tax_bracket.start", range.start);
      onChange("tax_info.tax_bracket.end", range.end);
    }
  };

  const getTaxBracketFormValue = (_path, fallback) => {
    const storedStart = getFormValue("tax_info.tax_bracket.start", null);
    const storedEnd = getFormValue("tax_info.tax_bracket.end", null);
    if (storedStart !== null && storedEnd !== null) {
      const entry = Object.entries(TAX_BRACKET_MAP).find(
        ([, v]) => v.start === storedStart && v.end === storedEnd,
      );
      return entry ? entry[0] : fallback;
    }
    return fallback;
  };

  return (
    <div>
      {/* Header with Edit controls */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Household Information</h2>
        <EditControls
          isEditing={isEditing}
          onEdit={onEdit}
          onSave={onSave}
          onCancel={onCancel}
          saving={saving}
          disabled={Object.keys(validationErrors || {}).length > 0}
        />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className={`px-4 py-2.5 text-[0.9375rem] font-medium border-b-2 -mb-px transition-colors ${
              subTab === tab.key
                ? "text-emerald-700 border-emerald-500"
                : "text-gray-500 border-transparent hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Income Details */}
      {subTab === "incomeDetails" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
          {showIncomeDetails(brokerageFlags, household?.accounts) && (
            <DataField
              label="Annual Income"
              value={formatCurrency(household.annual_income)}
              isEditable
              isEditing={isEditing}
              type="number"
              fieldPath="annual_income"
              onFieldChange={handleFieldChange}
              getFormValue={getFormValue}
              disableWheel
              error={validationErrors?.annual_income}
              onFieldError={onFieldError}
            />
          )}
          {household?.aum_availability !== 3 && (
            <DataField
              label="AUM"
              value={formatCurrency(household.aum)}
              isEditable={false}
              isEditing={isEditing}
              type="number"
              fieldPath="aum"
              onFieldChange={handleFieldChange}
              getFormValue={getFormValue}
              disableWheel
              error={validationErrors?.aum}
              onFieldError={onFieldError}
            />
          )}
          <DataField
            label="Tax Bracket"
            value={taxBracketToDisplay(household.tax_info)}
            isEditable
            isEditing={isEditing}
            options={TAX_BRACKET_OPTIONS}
            fieldPath="tax_info.tax_bracket_display"
            onFieldChange={handleTaxBracketChange}
            getFormValue={getTaxBracketFormValue}
          />
          {showIncomeDetails(brokerageFlags, household?.accounts) && (
            <>
              <DataField
                label="Estimated Liquid Net Worth"
                value={formatCurrency(household.estimated_liquid_net_worth)}
                isEditable
                isEditing={isEditing}
                type="number"
                fieldPath="estimated_liquid_net_worth"
                onFieldChange={handleFieldChange}
                getFormValue={getFormValue}
                disableWheel
                error={validationErrors?.estimated_liquid_net_worth}
                onFieldError={onFieldError}
              />
              <DataField
                label="Estimated Total Net Worth"
                value={formatCurrency(household.estimated_total_net_worth)}
                isEditable
                isEditing={isEditing}
                type="number"
                fieldPath="estimated_total_net_worth"
                onFieldChange={handleFieldChange}
                getFormValue={getFormValue}
                disableWheel
                error={validationErrors?.estimated_total_net_worth}
                onFieldError={onFieldError}
              />
            </>
          )}
          {showAnnualExpenseRange(brokerageFlags) && (
            <DataField
              label="Annual Expense Range"
              value={formatRange(household.annual_expense_range)}
              isEditable
              isEditing={isEditing}
              type="number"
              isRange
              rangeData={{
                start: household.annual_expense_range?.start,
                end: household.annual_expense_range?.end,
              }}
              fieldPath="annual_expense_range"
              onFieldChange={handleFieldChange}
              getFormValue={getFormValue}
              disableWheel
              error={validationErrors?.annual_expense_range}
              onFieldError={onFieldError}
            />
          )}
          {showSpecialExpenseRange(brokerageFlags) && (
            <DataField
              label="Special Expense Range"
              value={formatRange(household.special_expense_range)}
              isEditable
              isEditing={isEditing}
              type="number"
              isRange
              rangeData={{
                start: household.special_expense_range?.start,
                end: household.special_expense_range?.end,
              }}
              fieldPath="special_expense_range"
              onFieldChange={handleFieldChange}
              getFormValue={getFormValue}
              disableWheel
              error={validationErrors?.special_expense_range}
              onFieldError={onFieldError}
            />
          )}
          {showHouseholdSourceOfWealth(brokerageFlags) && (
            <DataField
              label="Source of Wealth"
              value={household.source_of_wealth || ""}
              isEditable
              isEditing={isEditing}
              options={SOURCE_OF_WEALTH_OPTIONS}
              fieldPath="source_of_wealth"
              onFieldChange={handleFieldChange}
              getFormValue={getFormValue}
            />
          )}
          {showBankingEstimates(brokerageFlags) && (
            <>
              <DataField
                label="Checks per month"
                value={household.banking_estimates?.no_of_checks_per_month ?? ""}
                isEditable
                isEditing={isEditing}
                type="number"
                min={0}
                fieldPath="banking_estimates.no_of_checks_per_month"
                onFieldChange={handleFieldChange}
                getFormValue={getFormValue}
                disableWheel
              />
              <DataField
                label="Domestic wire transfers per month"
                value={household.banking_estimates?.no_of_domestic_wire_transfers_per_month ?? ""}
                isEditable
                isEditing={isEditing}
                type="number"
                min={0}
                fieldPath="banking_estimates.no_of_domestic_wire_transfers_per_month"
                onFieldChange={handleFieldChange}
                getFormValue={getFormValue}
                disableWheel
              />
              <DataField
                label="International wire transfers per month"
                value={household.banking_estimates?.no_of_international_wire_transfers_per_month ?? ""}
                isEditable
                isEditing={isEditing}
                type="number"
                min={0}
                fieldPath="banking_estimates.no_of_international_wire_transfers_per_month"
                onFieldChange={handleFieldChange}
                getFormValue={getFormValue}
                disableWheel
              />
            </>
          )}
        </div>
      )}

      {/* Qualifying Questions */}
      {subTab === "qualifyingQuestions" && (
        <div className="space-y-1 max-w-lg">
          <ToggleField
            label="Accredited Investor"
            value={household.investor_associations?.accredited_investor ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.accredited_investor"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          <ToggleField
            label="Qualified Purchaser"
            value={household.investor_associations?.qualified_purchaser ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.qualified_purchaser"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          <ToggleField
            label="US Broker Dealer Association"
            value={household.investor_associations?.us_broker_dealer_association ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.us_broker_dealer_association"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          <ToggleField
            label="Broker Dealer Employee"
            value={household.investor_associations?.broker_dealer_employee ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.broker_dealer_employee"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          <ToggleField
            label="RIA Association"
            value={household.investor_associations?.ria_association ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.ria_association"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          <ToggleField
            label="Shareholder/Board Director"
            value={household.investor_associations?.shareholder_or_member_board_director ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.shareholder_or_member_board_director"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          <ToggleField
            label="Trades Low-Priced Securities"
            value={household.investor_associations?.low_priced_securities ?? false}
            isEditable
            isEditing={isEditing}
            fieldPath="investor_associations.low_priced_securities"
            onFieldChange={handleFieldChange}
            formData={formData}
          />
          {showGoldmanQualifiers(brokerageFlags) && (
            <>
              <ToggleField
                label="FINRA Affiliation"
                value={household.investor_associations?.finra_affiliation ?? false}
                isEditable
                isEditing={isEditing}
                fieldPath="investor_associations.finra_affiliation"
                onFieldChange={handleFieldChange}
                formData={formData}
              />
              <ToggleField
                label="Trades Marijuana-Related Securities"
                value={household.investor_associations?.marijuana_securities ?? false}
                isEditable
                isEditing={isEditing}
                fieldPath="investor_associations.marijuana_securities"
                onFieldChange={handleFieldChange}
                formData={formData}
              />
              <ToggleField
                label="Politically Exposed Person"
                value={household.investor_associations?.politically_exposed_person ?? false}
                isEditable
                isEditing={isEditing}
                fieldPath="investor_associations.politically_exposed_person"
                onFieldChange={handleFieldChange}
                formData={formData}
              />
            </>
          )}
        </div>
      )}

      {/* Investment Experience */}
      {subTab === "investmentExperience" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
          {[
            { key: "stocks", label: "Stocks" },
            { key: "bonds", label: "Bonds" },
            { key: "mutual_funds", label: "Mutual Funds" },
            { key: "options", label: "Options" },
            { key: "alternative_investments", label: "Alternative Investments" },
            { key: "variable_annuities", label: "Variable Annuities" },
            { key: "partnerships", label: "Partnerships" },
          ]
            .filter(({ key }) => showExperienceField(key, brokerageFlags, household?.accounts))
            .map(({ key, label }) => (
            <DataField
              key={key}
              label={`${label} (years)`}
              value={
                household.investor_experience?.[key] !== undefined &&
                household.investor_experience?.[key] !== null
                  ? `${household.investor_experience[key]} years`
                  : "N/A"
              }
              isEditable
              isEditing={isEditing}
              type="number"
              min={0}
              max={99}
              fieldPath={`investor_experience.${key}`}
              onFieldChange={handleFieldChange}
              getFormValue={getFormValue}
              disableWheel
              error={validationErrors?.[`investor_experience.${key}`]}
              onFieldError={onFieldError}
            />
          ))}
        </div>
      )}

    </div>
  );
};

export default ReviewHouseholdInfoTab;
